import React from "react";
import { ArrowRight, CheckCircle2, Star } from "lucide-react";
import CmsEditableList from "./cms/CmsEditableList";
import CmsEditableText from "./cms/CmsEditableText";

function About({ cms }) {
  const fallback = {
    eyebrow: "About Bobbin USA",
    headline: "High-quality bobbins and textile components built to perform",
    body:
      "Bobbin USA manufactures durable, precision-engineered bobbins and textile components for modern industrial applications. The focus is reliability, clean output, and custom solutions that stay consistent under real production demands.",
    primaryCtaLabel: "Read More",
    primaryCtaHref: "/contact",
    trustNote: "Built for dependable output",
    sideEyebrow: "Visual direction",
    sideHeadline: "Clean, modern, and production-focused",
    sideNote:
      "The visual system stays close to the home page, so About reads as part of the same site instead of a separate template.",
  };
  const stats = cms?.json("about-preview", "stats", [
    { value: "25+", label: "Years of know-how" },
    { value: "100%", label: "Built for performance" },
    { value: "USA", label: "Trusted by production teams" },
  ]) || [];
  const visualItems = cms?.json("about-preview", "visualItems", [
    { title: "Primary blue", text: "#3991D5 and #70C6FE anchor the brand tone." },
    { title: "Soft contrast", text: "White surfaces and pale accent blocks keep the layout airy." },
    { title: "Editorial balance", text: "Bold Gopher headlines paired with calm supporting text." },
  ]) || [];
  const text = (fieldKey, fallbackValue) => cms?.text("about-preview", fieldKey, fallbackValue) || fallbackValue;

  return (
    <section className="bg-gradient-to-b from-white via-[#f8fbff] to-[#e9f4ff] py-16">
      <div className="container">
        <div className="grid overflow-hidden rounded-[32px] border border-[#3991D5]/10 bg-white shadow-[0_18px_60px_rgba(57,145,213,0.12)] lg:grid-cols-[1.15fr_0.85fr]">
          <div className="relative overflow-hidden px-6 py-8 sm:px-10 sm:py-12">
            <div className="absolute left-0 top-0 h-28 w-28 rounded-br-full bg-[#70C6FE]/20" />
            <div className="absolute bottom-0 right-0 h-40 w-40 rounded-tl-full bg-[#EAEBB8]/35" />

            <div className="relative z-10 max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-[#3991D5]/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#1278b5]">
                <Star className="h-3.5 w-3.5" />
                <CmsEditableText editMode={cms?.editMode} fieldKey="eyebrow" onSave={cms?.updateField} sectionKey="about-preview" value={text("eyebrow", fallback.eyebrow)} />
              </span>

              <CmsEditableText as="h3" className="mt-5 font-gopher text-3xl font-black uppercase leading-[0.95] text-[#0f172a] sm:text-5xl" editMode={cms?.editMode} fieldKey="headline" multiline onSave={cms?.updateField} sectionKey="about-preview" value={text("headline", fallback.headline)} />

              <CmsEditableText as="p" className="mt-5 max-w-xl font-pmesans text-base leading-7 text-slate-600 sm:text-lg" editMode={cms?.editMode} fieldKey="body" multiline onSave={cms?.updateField} sectionKey="about-preview" value={text("body", fallback.body)} />

              <div className="mt-8 grid gap-3 sm:grid-cols-3">
                <div className="sm:col-span-3">
                  <CmsEditableList editMode={cms?.editMode} fieldKey="stats" fields={["value", "label"]} itemName="stat box" label="Homepage stat boxes" onSave={cms?.updateField} sectionKey="about-preview" value={stats} />
                </div>
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl border border-[#3991D5]/10 bg-[#f8fbff] p-4"
                  >
                    <div className="font-gopher text-2xl font-black text-[#1278b5]">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-sm text-slate-600">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={text("primaryCtaHref", fallback.primaryCtaHref)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#3991D5] px-6 py-3 font-gopher text-sm font-bold uppercase tracking-wide text-white transition-transform hover:scale-[1.02]"
                >
                  <CmsEditableText editMode={cms?.editMode} fieldKey="primaryCtaLabel" onSave={cms?.updateField} sectionKey="about-preview" value={text("primaryCtaLabel", fallback.primaryCtaLabel)} />
                  <ArrowRight className="h-4 w-4" />
                </a>
                <div className="inline-flex items-center gap-2 rounded-full border border-[#3991D5]/15 bg-white px-5 py-3 text-sm text-slate-600">
                  <CheckCircle2 className="h-4 w-4 text-[#3991D5]" />
                  <CmsEditableText editMode={cms?.editMode} fieldKey="trustNote" onSave={cms?.updateField} sectionKey="about-preview" value={text("trustNote", fallback.trustNote)} />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#3991D5] px-6 py-8 text-white sm:px-10 sm:py-12">
            <div className="flex h-full flex-col justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/75">
                  <CmsEditableText editMode={cms?.editMode} fieldKey="sideEyebrow" onSave={cms?.updateField} sectionKey="about-preview" value={text("sideEyebrow", fallback.sideEyebrow)} />
                </p>
                <CmsEditableText as="h4" className="mt-3 font-gopher text-3xl font-black uppercase leading-[0.95] sm:text-4xl" editMode={cms?.editMode} fieldKey="sideHeadline" multiline onSave={cms?.updateField} sectionKey="about-preview" value={text("sideHeadline", fallback.sideHeadline)} />
              </div>

              <div className="mt-10 grid gap-4">
                <CmsEditableList editMode={cms?.editMode} fieldKey="visualItems" fields={["title", "text"]} itemName="visual box" label="Homepage visual boxes" onSave={cms?.updateField} sectionKey="about-preview" value={visualItems} />
                {visualItems.map((item) => (
                  <VisualItem key={item.title} title={item.title} text={item.text} />
                ))}
              </div>

              <div className="mt-10 rounded-[28px] border border-white/15 bg-white/10 p-5 backdrop-blur-sm">
                <CmsEditableText as="p" className="text-sm leading-7 text-white/90" editMode={cms?.editMode} fieldKey="sideNote" multiline onSave={cms?.updateField} sectionKey="about-preview" value={text("sideNote", fallback.sideNote)} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VisualItem({ title, text }) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
      <div className="font-gopher text-lg font-bold uppercase tracking-wide">
        {title}
      </div>
      <p className="mt-2 text-sm leading-6 text-white/85">{text}</p>
    </div>
  );
}

export default About;
